import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeUrl, SafeResourceUrl } from '@angular/platform-browser';
import { ArquivoAzure } from 'src/app/shared/models/arquivo/ArquivoAzure';
import { ArquivoService } from 'src/app/shared/services/arquivo.service';

@Component({
  selector: 'app-visualizar-arquivo',
  templateUrl: './visualizar-arquivo.component.html',
  styleUrls: ['./visualizar-arquivo.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class VisualizarArquivoComponent implements OnInit {
  @Input() arquivo!: ArquivoAzure;
  @Output() fechar = new EventEmitter<void>();

  isLoading = true;
  loadError = false;
  private dataUrl: SafeUrl | null = null;
  private resourceUrl: SafeResourceUrl | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private arquivoService: ArquivoService
  ) {}

  ngOnInit(): void {
    this.loadArquivo();
  }

  private loadArquivo(): void {
    if (!this.arquivo.nome) {
      this.isLoading = false;
      this.loadError = true;
      return;
    }

    // If content is already loaded (Base64), use it directly
    if (this.arquivo.conteudo) {
      this.processarConteudo(this.arquivo.conteudo);
      return;
    }

    // Otherwise, download from Azure
    if (this.arquivo.caminhoAzure) {
      this.arquivoService.getArquivoPorNome(this.arquivo.nome).subscribe({
        next: (blob: Blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result as string;
            const base64Content = base64.split(',')[1]; // Remove data:image/... prefix
            this.arquivo.conteudo = base64Content;
            this.arquivo.ehBase64 = true;
            this.processarConteudo(base64Content);
          };
          reader.readAsDataURL(blob);
        },
        error: (err) => {
          console.error('Erro ao carregar arquivo:', err);
          this.isLoading = false;
          this.loadError = true;
        }
      });
    } else {
      this.isLoading = false;
      this.loadError = true;
    }
  }

  private processarConteudo(base64Content: string): void {
    this.dataUrl = this.sanitizer.bypassSecurityTrustUrl(
      `data:${this.getMimeType()};base64,${base64Content}`
    );
    this.resourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `data:${this.getMimeType()};base64,${base64Content}`
    );
    this.isLoading = false;
  }

  onFecharClick(): void {
    this.fechar.emit();
  }

  getFileType(): 'image' | 'video' | 'audio' | 'pdf' | 'unknown' {
    if (!this.arquivo.nome) return 'unknown';

    const nome = this.arquivo.nome.toLowerCase();
    if (nome.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) return 'image';
    if (nome.match(/\.(mp4|webm|ogg|mov)$/)) return 'video';
    if (nome.match(/\.(mp3|wav|ogg)$/)) return 'audio';
    if (nome.match(/\.pdf$/)) return 'pdf';

    return 'unknown';
  }

  getMimeType(): string {
    if (!this.arquivo.nome) return 'application/octet-stream';

    const nome = this.arquivo.nome.toLowerCase();

    // Image MIME types
    if (nome.endsWith('.jpg') || nome.endsWith('.jpeg')) return 'image/jpeg';
    if (nome.endsWith('.png')) return 'image/png';
    if (nome.endsWith('.gif')) return 'image/gif';
    if (nome.endsWith('.bmp')) return 'image/bmp';
    if (nome.endsWith('.webp')) return 'image/webp';

    // Video MIME types
    if (nome.endsWith('.mp4')) return 'video/mp4';
    if (nome.endsWith('.webm')) return 'video/webm';
    if (nome.endsWith('.ogg') || nome.endsWith('.ogv')) return 'video/ogg';
    if (nome.endsWith('.mov')) return 'video/quicktime';

    // Audio MIME types
    if (nome.endsWith('.mp3')) return 'audio/mpeg';
    if (nome.endsWith('.wav')) return 'audio/wav';
    if (nome.endsWith('.ogg')) return 'audio/ogg';

    // PDF
    if (nome.endsWith('.pdf')) return 'application/pdf';

    return 'application/octet-stream';
  }

  isImage(): boolean {
    return this.getFileType() === 'image';
  }

  isVideo(): boolean {
    return this.getFileType() === 'video';
  }

  isAudio(): boolean {
    return this.getFileType() === 'audio';
  }

  isPdf(): boolean {
    return this.getFileType() === 'pdf';
  }

  getDataUrl(): SafeUrl | null {
    return this.dataUrl;
  }

  getResourceUrl(): SafeResourceUrl | null {
    return this.resourceUrl;
  }
}
